/**
 * The desk's data: filters in, a page of domains out.
 *
 * Kept out of the section component for the usual reason and one specific one:
 * the search box types a character at a time and the coverage filter is applied
 * on the server AFTER the page is folded, so "how many rows came back" and "how
 * many were asked for" are different numbers. Both live here, where the fetch
 * that produced them lives, rather than being recomputed in the markup.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import {
  EMPTY_AUDIT_FILTERS,
  listSiteAudits,
  type AuditDeskPage,
  type AuditFilters,
} from "../../../lib/siteAudits";

/** How long the search box waits before asking. Long enough to type a domain. */
const SEARCH_DEBOUNCE_MS = 300;

export function useSiteAudits() {
  const [filters, setFilters] = useState<AuditFilters>(EMPTY_AUDIT_FILTERS);
  const [page, setPage] = useState(0);
  const [data, setData] = useState<AuditDeskPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  /** Bumped by `reload()`. A dependency that is not a filter, so a manual
   *  refresh does not have to pretend the query changed. */
  const [nonce, setNonce] = useState(0);

  const abort = useRef<AbortController | null>(null);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      abort.current?.abort();
      const ctrl = new AbortController();
      abort.current = ctrl;
      setLoading(true);
      setError(false);
      listSiteAudits(filters, page, ctrl.signal)
        .then((res) => {
          if (ctrl.signal.aborted) return;
          setData(res);
          setLoading(false);
        })
        .catch(() => {
          // An abort is this hook replacing its own request, not a failure. The
          // screen it belongs to has already asked for something newer.
          if (ctrl.signal.aborted) return;
          setError(true);
          setLoading(false);
        });
      // Only the free-text box is worth debouncing; a filter click is one event
      // and waiting on it just makes the screen feel slow.
    }, filters.q ? SEARCH_DEBOUNCE_MS : 0);
    return () => window.clearTimeout(timer);
  }, [filters, page, nonce]);

  useEffect(() => () => abort.current?.abort(), []);

  /** Changing a filter goes back to the first page. Staying on page four of a
   *  query that no longer has four pages shows an empty screen and no reason. */
  const setFilter = useCallback(<K extends keyof AuditFilters>(key: K, value: AuditFilters[K]) => {
    setFilters((f) => ({ ...f, [key]: value }));
    setPage(0);
  }, []);

  const reset = useCallback(() => {
    setFilters(EMPTY_AUDIT_FILTERS);
    setPage(0);
  }, []);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  return {
    filters,
    setFilter,
    reset,
    page,
    setPage,
    rows: data?.rows ?? [],
    totals: data?.totals ?? null,
    hasMore: data?.hasMore ?? false,
    filteredOut: data?.filteredOut ?? 0,
    loading,
    error,
    reload,
    /** True when a filter is doing something, so the UI can offer to undo it. */
    filtered:
      filters.q.trim() !== "" || filters.coverage !== "all" || filters.status !== "all",
  };
}
